import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Sparkles, Globe, Zap, Lock } from "lucide-react";
import useSEO from "@/lib/useSEO";
import VisitorCounter from "@/components/VisitorCounter";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/63a57259-d272-4099-a07a-ad9d443a994a/images/1f647185bfdd3e8c7cc275adcbe4c76ff88a62af8df530f3b4f76bd877fd9b50.png";
const CARD_IMG = "https://static.prod-images.emergentagent.com/jobs/63a57259-d272-4099-a07a-ad9d443a994a/images/d16caaeb38589f59d5516de9caa20bc49fff4c9a64f58c9b69db7538b5360017.png";
const DASH_IMG = "https://static.prod-images.emergentagent.com/jobs/63a57259-d272-4099-a07a-ad9d443a994a/images/c98c47568ebe2ecb2a27f9a03d1e1f126a4b31fe762db4f1af2ae13fc3655900.png";
const PORTRAIT = "https://images.unsplash.com/photo-1763336016195-1942264993d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBsaWZlc3R5bGUlMjBwb3J0cmFpdHxlbnwwfHx8fDE3Nzg2MDE2MzF8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  useSEO({
    description: "Universal payment platform — branded payment links, P2P transfers, multi-currency wallet, merchant payouts. Crafted by Y BETHALPHA SRL.",
    path: "/",
  });
  return (
    <main data-testid="landing-page" className="bg-bg text-white">
      {/* HERO */}
      <section className="relative grain overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-bg" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 lux-rise">
              <span data-testid="hero-eyebrow" className="text-xs uppercase tracking-[0.3em] text-gold">
                INFINITE TIMES— Universal Payments. Refined.
              </span>
              <h1 data-testid="hero-title" className="font-display text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tighter mt-6 font-light">
                Move money like
                <br />
                <span className="italic gold-text">private wealth.</span>
              </h1>
              <p data-testid="hero-subtitle" className="mt-8 text-secondary-fg text-base sm:text-lg max-w-xl leading-relaxed">
                y ℶα unifies merchant checkout, peer transfers, and digital wallets into one quiet, formidable platform. Built for those who treat every transaction as a signature.
              </p>
              <div className="mt-12 flex flex-wrap items-center gap-5">
                <Link to="/register" data-testid="hero-cta-primary" className="btn-gold inline-flex items-center gap-2">
                  Open an account <ArrowUpRight size={16} />
                </Link>
                <Link to="/login" data-testid="hero-cta-secondary" className="btn-ghost">
                  Sign in
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 lux-rise" style={{ animationDelay: "0.2s" }}>
              <div className="relative">
                <img src={CARD_IMG} alt="Premium card" className="w-full max-w-sm ml-auto drop-shadow-2xl" />
                <div className="absolute -bottom-4 right-0 text-right">
                  <div className="text-xs uppercase tracking-[0.3em] text-muted-fg">Cardholder</div>
                  <div className="font-display text-xl">y ℶα Noir</div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-32 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border hairline-strong">
            {[
              ["$2.4B+", "Processed"],
              ["0.0%", "Hidden fees"],
              ["190+", "Countries"],
              ["3s", "Average payout"],
            ].map(([v, l]) => (
              <div key={l} className="bg-bg p-6 lg:p-8">
                <div className="font-display text-4xl lg:text-5xl gold-text">{v}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-muted-fg">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROPOSITION */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— 01 / Atelier</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light">
                One vault.
                <br />
                <span className="italic">Every rail.</span>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-12 border-l hairline">
              <p className="text-lg text-secondary-fg leading-relaxed font-light max-w-xl">
                Accept card payments through Stripe Checkout. Send invoices as elegant payment links. Move money to other y ℶα members in three taps. Top up your wallet from any card. It's a complete financial movement — without the noise.
              </p>
            </div>
          </div>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border hairline-strong">
            {[
              { icon: <ReceiptIcon />, title: "Payment Links", body: "Compose a payable moment in seconds. Send by link, charge by card, receive in your wallet.", to: "/atelier/payment-links", testid: "atelier-card-payment-links" },
              { icon: <WalletIcon />, title: "P2P Transfers", body: "Move funds between y ℶα members instantly. Search by email, send with a note.", to: "/atelier/p2p-transfers", testid: "atelier-card-p2p" },
              { icon: <ShieldCheck size={28} strokeWidth={1.2} />, title: "Vault-Grade Security", body: "Stripe-grade encryption, JWT tokens, and Google sign-in. Your sessions live behind iron.", to: "/atelier/security", testid: "atelier-card-security" },
            ].map((f, i) => (
              <Link key={i} to={f.to} data-testid={f.testid} target="_blank" rel="noopener noreferrer" className="bg-bg p-10 hover:bg-surface transition-colors duration-500 group block">
                <div className="text-gold mb-6 group-hover:scale-110 transition-transform duration-500 inline-flex">{f.icon}</div>
                <h3 className="font-display text-2xl mb-3">{f.title}</h3>
                <p className="text-secondary-fg leading-relaxed font-light">{f.body}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold opacity-0 group-hover:opacity-100 transition-opacity">
                  Discover <ArrowUpRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DASHBOARD */}
      <section className="relative py-32 border-t hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-12 items-center">
            <div className="col-span-12 lg:col-span-6 order-2 lg:order-1">
              <img src={DASH_IMG} alt="Dashboard" className="rounded-lg border hairline-strong shadow-2xl" />
            </div>
            <div className="col-span-12 lg:col-span-6 order-1 lg:order-2">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— 02 / For Merchants</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light">
                A console
                <br />
                <span className="italic gold-text">worthy of your craft.</span>
              </h2>
              <p className="mt-8 text-secondary-fg leading-relaxed text-lg max-w-md font-light">
                Composed in obsidian and gold. Watch your revenue accrue in real time. Configure links, dispatch invoices, withdraw to any rail.
              </p>
              <ul className="mt-10 space-y-4">
                {[
                  "Stripe Checkout — cards, wallets, BNPL",
                  "Unlimited payment links with custom branding",
                  "Live transaction ledger with filters",
                  "Instant wallet payouts between accounts",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-3 text-secondary-fg">
                    <span className="text-gold mt-1.5">◆</span> {s}
                  </li>
                ))}
              </ul>
              <Link to="/register" data-testid="merchant-cta" className="btn-gold inline-flex items-center gap-2 mt-12">
                Become a merchant <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOMER */}
      <section className="relative py-32 border-t hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-12 items-center">
            <div className="col-span-12 lg:col-span-6">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— 03 / For Patrons</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light">
                Spend with
                <br />
                <span className="italic">discretion.</span>
              </h2>
              <p className="mt-8 text-secondary-fg leading-relaxed text-lg max-w-md font-light">
                Hold a y ℶα wallet. Top up from any card. Send to a friend by email. Pay any merchant's link with a single tap.
              </p>
              <div className="mt-10 grid grid-cols-2 gap-px bg-white/5 border hairline-strong">
                {[
                  ["Sparkle", "Concierge UI", Sparkles, "/patrons/concierge-ui", "patron-card-concierge"],
                  ["Globe", "Multi-currency", Globe, "/patrons/multi-currency", "patron-card-multi-currency"],
                  ["Zap", "Instant transfer", Zap, "/patrons/instant-transfer", "patron-card-instant"],
                  ["Lock", "Vaulted sessions", Lock, "/patrons/vaulted-sessions", "patron-card-vaulted"],
                ].map(([k, label, Icon, to, testid]) => (
                  <Link
                    key={k}
                    to={to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={testid}
                    className="bg-bg p-6 group hover:bg-surface transition-colors duration-500"
                  >
                    <Icon className="text-gold mb-3 group-hover:scale-110 transition-transform duration-500" size={18} />
                    <div className="text-sm flex items-center justify-between">
                      <span>{label}</span>
                      <ArrowUpRight size={14} className="text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>
                ))}
              </div>
              <Link to="/register" data-testid="customer-cta" className="btn-gold inline-flex items-center gap-2 mt-12">
                Join y ℶα <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="col-span-12 lg:col-span-6">
              <div className="relative">
                <img src={PORTRAIT} alt="" className="rounded-lg w-full h-[520px] object-cover grayscale-[0.2]" />
                <div className="absolute bottom-6 left-6 right-6 glass p-6 rounded-md">
                  <div className="text-xs tracking-[0.3em] text-gold">A Patron of&nbsp; <span className="text-sm">y</span> ℶα</div>
                  <div className="font-display text-2xl mt-2">"It's quiet. That's the luxury."</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 border-t hairline">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-5xl lg:text-7xl tracking-tighter font-light">
            <span className="italic gold-text">Money,</span> reinterpreted.
          </h2>
          <p className="mt-8 text-secondary-fg max-w-xl mx-auto leading-relaxed font-light">
            Open your account in under sixty seconds. No paperwork. No noise. Just movement.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-5">
            <Link to="/register" data-testid="footer-cta" className="btn-gold">Get y ℶα</Link>
            <Link to="/login" className="btn-ghost">Sign in</Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-32 pt-10 border-t hairline">
          <div className="flex justify-center mb-6">
            <VisitorCounter />
          </div>
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div className="font-display text-lg silver-text">{new Date().getFullYear()} <span className="text-base">ℶα</span></div>
            <div data-testid="footer-copyright" className="text-[10px] text-muted-fg uppercase tracking-[0.2em]">© {new Date().getFullYear()} I Y BETHALPHA SRL. All Rights Reserved</div>
          </div>
        </div>
      </section>
    </main>
  );
}

// inline icon helpers (kept here to keep file self-contained)
function ReceiptIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M4 4h16v18l-3-2-3 2-3-2-3 2-3-2L4 22z"/><path d="M8 10h8M8 14h5"/></svg>
  );
}
function WalletIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="3" y="6" width="18" height="14" rx="2"/><path d="M16 13a1 1 0 100-2 1 1 0 000 2zM3 10h18"/></svg>
  );
}
